/**
 * @fileoverview Seção "Abordagens inteligentes" do Dashboard (só admin; com a
 * regra nova ligada, quem tem smart_approaches.view). No período da página,
 * mostra:
 * - quantas abordagens começaram, quantas tiveram resposta (e depois de qual
 *   mensagem), quantas ficaram sem resposta e a taxa;
 * - os filtros de fluxo e de template inicial;
 * - o gráfico das respostas por mensagem;
 * - a tabela por fluxo ou por template.
 * Usa os cartões e as cores do Dashboard (`ui` e `colors` vêm da página). Na
 * recarga, os números anteriores ficam esmaecidos até chegarem os novos; se a
 * recarga falha, ficam esmaecidos com o aviso. As opções dos filtros vêm uma
 * vez (não dependem do período).
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, LabelList } from "recharts";
import moment from "moment";
import { Sparkles } from "lucide-react";

import api from "../../services/api";
import ModernSelect from "../../components/ModernSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { i18n } from "../../translate/i18n";
import { translateWithLiterals } from "../../utils/translateWithLiterals";
import { ordinal } from "../../utils/smartApproachText";
import { locale } from "../../components/Triage/helpers";

const t = (key, params) => i18n.t(`smartApproaches.dashboard.${key}`, params);
const tSmart = (key, params) => i18n.t(`smartApproaches.${key}`, params);

const ALL = "all";

const formatRate = (rate) =>
  rate === null || rate === undefined ? "—" : `${Number(rate).toLocaleString(locale(), { maximumFractionDigits: 1 })}%`;

const flowLabel = (name, deleted) => (deleted ? translateWithLiterals("smartApproaches.dashboard.deleted", { name }) : name);

// Valor primeiro, o que ele é depois (o leitor já sabe qual barra apontou).
const StepTooltip = ({ active, payload, colors }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: colors.tooltipBackground, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 6, padding: "6px 10px" }}>
      <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{d.replied}</div>
      <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>{t("repliedAfter", { step: ordinal(d.step, tSmart) })}</div>
    </div>
  );
};

const SmartApproachStats = ({ ui, colors, last24h, dateFrom, dateTo, hourFrom, hourTo, refreshKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const canView = usePermission().can("smart_approaches.view", isAdminUser(user));

  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ flows: [], templates: [] });
  // Opções já carregadas: as próximas leituras não pedem de novo.
  const optionsLoadedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [flowId, setFlowId] = useState(ALL);
  const [template, setTemplate] = useState(ALL);
  const [tableBy, setTableBy] = useState("flow");
  // Número da última leitura: resposta atrasada (outro período ou filtro) não sobrescreve a mais nova.
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!canView || !dateFrom || !dateTo) return;
    const current = (requestRef.current += 1);
    const [templateName, templateLanguage] = template === ALL ? [] : template.split("|");
    setLoading(true);
    try {
      const { data: body } = await api.get("/dashboard/smart-approaches", {
        params: {
          tz: moment().format("Z"),
          ...(last24h ? { rolling_24h: true } : { date_from: dateFrom, date_to: dateTo, hour_from: hourFrom, hour_to: hourTo }),
          ...(flowId !== ALL ? { flow_id: flowId } : {}),
          ...(templateName ? { template_name: templateName, template_language: templateLanguage } : {}),
          ...(optionsLoadedRef.current ? {} : { include_options: true }),
        },
      });
      if (body?.options) {
        optionsLoadedRef.current = true;
        setOptions(body.options);
      }
      if (current !== requestRef.current) return;
      setData(body);
      setFailed(false);
    } catch (err) {
      if (current === requestRef.current) setFailed(true);
    } finally {
      if (current === requestRef.current) setLoading(false);
    }
  }, [canView, last24h, dateFrom, dateTo, hourFrom, hourTo, flowId, template]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!canView) return null;

  const totals = data?.totals;
  const byStep = data?.byStep || [];
  const rows = (tableBy === "flow" ? data?.byFlow : data?.byTemplate) || [];
  const tiles = totals
    ? [
        { key: "started", label: t("started"), value: totals.started },
        {
          key: "replied",
          label: t("replied"),
          value: totals.replied,
          sub: byStep.map((s) => `${ordinal(s.step, tSmart)}: ${s.replied}`).join(" · "),
        },
        { key: "noReply", label: t("noReply"), value: totals.noReply },
        { key: "inProgress", label: t("inProgress"), value: totals.inProgress },
        { key: "rate", label: t("rate"), value: formatRate(totals.rate), help: t("rateHelp") },
      ]
    : [];
  const chartData = byStep.map((s) => ({ ...s, label: t("stepLabel", { step: ordinal(s.step, tSmart) }) }));
  const cell = { padding: "6px", borderBottom: `1px solid ${colors.cardBorderSoft}`, fontSize: 12, color: colors.textPrimary };
  const numCell = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const headCell = { ...cell, fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 };

  const renderBody = () => {
    if (!data) {
      return (
        <div style={{ padding: 18, textAlign: "center", fontSize: 12, color: colors.textMuted }}>
          {failed ? (
            <>
              {t("loadError")}{" "}
              <button type="button" onClick={load} style={{ background: "none", border: "none", padding: 0, color: colors.link, fontWeight: 600, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
                {t("retry")}
              </button>
            </>
          ) : (
            "…"
          )}
        </div>
      );
    }
    return (
      <div style={{ opacity: loading || failed ? 0.55 : 1, transition: "opacity 0.2s ease" }} data-smart-stats>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))", gap: 8, padding: "0 12px" }}>
          {tiles.map((tile) => (
            <div key={tile.key} data-tile={tile.key} style={{ background: colors.surface, border: `1px solid ${colors.cardBorderSoft}`, borderRadius: 8, padding: "8px 10px", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary }}>
                <span>{tile.label}</span>
                {tile.help && (
                  <Tooltip title={tile.help} arrow placement="top" interactive>
                    <span className={ui.hi}>?</span>
                  </Tooltip>
                )}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.25, marginTop: 2 }}>{tile.value}</div>
              {tile.sub && <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{tile.sub}</div>}
            </div>
          ))}
        </div>

        {totals.started === 0 ? (
          <div style={{ padding: 18, textAlign: "center", fontSize: 12, color: colors.textMuted }}>{t("empty")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 2fr) minmax(0, 3fr)", gap: 12, padding: "10px 12px 10px" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>{t("chartTitle")}</span>
                <Tooltip title={t("chartHelp")} arrow placement="top" interactive>
                  <span className={ui.hi}>?</span>
                </Tooltip>
              </div>
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={chartData} margin={{ top: 18, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={colors.chartGrid} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: colors.textSecondary }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: colors.textSubtle }} axisLine={false} tickLine={false} />
                  <RTooltip cursor={{ fill: colors.rowHover }} content={<StepTooltip colors={colors} />} />
                  <Bar dataKey="replied" fill={colors.textSecondary} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                    <LabelList dataKey="replied" position="top" style={{ fontSize: 11, fontWeight: 600, fill: colors.textPrimary }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 6 }} role="group" aria-label={t("tableLabel")}>
                {["flow", "template"].map((key) => (
                  <Chip
                    key={key}
                    size="small"
                    label={key === "flow" ? t("byFlow") : t("byTemplate")}
                    onClick={() => setTableBy(key)}
                    className={tableBy === key ? ui.fcOn : ui.fc}
                    aria-pressed={tableBy === key}
                  />
                ))}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }} data-smart-table={tableBy}>
                  <thead>
                    <tr>
                      <th style={{ ...headCell, textAlign: "left" }}>{tableBy === "flow" ? t("columns.flow") : t("columns.template")}</th>
                      <th style={{ ...headCell, textAlign: "right" }}>{t("columns.started")}</th>
                      <th style={{ ...headCell, textAlign: "right" }}>{t("columns.replied")}</th>
                      {/* No celular, "Sem resposta" fica só nos números de cima: a taxa cabe na tela. */}
                      {!isMobile && <th style={{ ...headCell, textAlign: "right" }}>{t("columns.noReply")}</th>}
                      <th style={{ ...headCell, textAlign: "right" }}>{t("columns.rate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      // Sem id: o fluxo foi apagado de vez (com a conexão).
                      const deleted = row.flowId === null || options.flows.some((f) => f.id === row.flowId && f.deleted);
                      const name = tableBy === "flow" ? flowLabel(row.flowName, deleted) : `${row.templateName} (${row.language})`;
                      return (
                        <tr key={tableBy === "flow" ? `${row.flowId}-${row.flowName}` : `${row.templateName}|${row.language}`}>
                          <td style={{ ...cell, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
                            {name}
                          </td>
                          <td style={numCell}>{row.started}</td>
                          <td style={numCell}>{row.replied}</td>
                          {!isMobile && <td style={numCell}>{row.noReply}</td>}
                          <td style={{ ...numCell, fontWeight: 600 }}>{formatRate(row.rate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={ui.c} style={{ marginTop: 12 }} data-smart-section>
      <div className={ui.sHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Sparkles size={14} strokeWidth={2} style={{ color: colors.textSecondary }} aria-hidden="true" />
          <span className={ui.sTitle}>{t("title")}</span>
          <Tooltip title={t("help")} arrow placement="top" interactive>
            <span className={ui.hi}>?</span>
          </Tooltip>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <ModernSelect
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            className={ui.fs}
            formControlProps={{ size: "small" }}
            SelectDisplayProps={{ "aria-label": t("flowFilter") }}
          >
            <MenuItem value={ALL}>{t("allFlows")}</MenuItem>
            {options.flows.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {flowLabel(f.name, f.deleted)}
              </MenuItem>
            ))}
          </ModernSelect>
          <ModernSelect
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className={ui.fs}
            formControlProps={{ size: "small" }}
            SelectDisplayProps={{ "aria-label": t("templateFilter") }}
          >
            <MenuItem value={ALL}>{t("allTemplates")}</MenuItem>
            {options.templates.map((tpl) => (
              <MenuItem key={`${tpl.templateName}|${tpl.language}`} value={`${tpl.templateName}|${tpl.language}`}>
                {`${tpl.templateName} (${tpl.language})`}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      </div>
      {failed && data && (
        <div role="alert" style={{ padding: "0 12px 8px", fontSize: 12, color: colors.textSecondary }}>
          {t("refreshError")}{" "}
          <button type="button" onClick={load} style={{ background: "none", border: "none", padding: 0, color: colors.link, fontWeight: 600, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
            {t("retry")}
          </button>
        </div>
      )}
      <div style={{ paddingBottom: 6 }}>{renderBody()}</div>
    </div>
  );
};

export default SmartApproachStats;
